import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    // Obtrusiveness: a muted looping background video keeps decoding/compositing
    // even when the tab is backgrounded or the section is scrolled off-screen,
    // which pins WindowServer/CPU on macOS and can grab the system audio session
    // (ducking Spotify etc). Gate playback on BOTH tab visibility (Page
    // Visibility API) and on-screen presence (IntersectionObserver) so it only
    // runs when it's actually being looked at. No change to the live look.
    this.#inView = true;
    document.addEventListener('visibilitychange', this.#syncPlayback);

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          this.#inView = entries[0].isIntersecting;
          this.#syncPlayback();
        },
        { threshold: 0.1 }
      );
      this.#observer.observe(videoElement);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('visibilitychange', this.#syncPlayback);
    this.#observer?.disconnect();
  }

  /** @type {boolean} */
  #inView = true;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  // Arrow-function field (not a method) so it's stably bound for
  // addEventListener/removeEventListener and safe to reference directly.
  #syncPlayback = () => {
    const { videoElement } = this.refs;
    if (!videoElement) return;

    if (this.#inView && !document.hidden) {
      const playPromise = videoElement.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
    } else if (!videoElement.paused) {
      videoElement.pause();
    }
  };
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
